import { useMutation, useQueryClient } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import { AddCollaboratorForm, UpdateCollaboratorForm } from "../schemas";
import { collaboratorQueryKeys } from "../query-keys/collaborator.query-keys";
import { isAxiosError } from "axios";

const COLLABORATOR_BASE_URL = "/collaborator";
const AC_EXPO_COLLABORATOR_URL = `${COLLABORATOR_BASE_URL}/ac-expo`;

const addCollaborator = async (
  data: AddCollaboratorForm,
  asAcExpo?: boolean
) => {
  const url = asAcExpo ? AC_EXPO_COLLABORATOR_URL : COLLABORATOR_BASE_URL;

  const { data: response } = await privateAxios.post(url, data);
  return response;
};

export const useAddCollaboratorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      asAcExpo,
    }: {
      data: AddCollaboratorForm;
      asAcExpo?: boolean;
    }) => addCollaborator(data, asAcExpo),
    onSuccess: (_data, { asAcExpo }) => {
      queryClient.invalidateQueries({
        queryKey: collaboratorQueryKeys.lists(asAcExpo),
      });
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        throw {
          ...error,
          message: error.response?.data.message,
        };
      }
    },
  });
};

const removeCollaborator = async (id: string, asAcExpo?: boolean) => {
  const url = asAcExpo ? AC_EXPO_COLLABORATOR_URL : COLLABORATOR_BASE_URL;

  const { data: response } = await privateAxios.delete(`${url}/${id}`);
  return response;
};

export const useDeleteCollaboratorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, asAcExpo }: { id: string; asAcExpo?: boolean }) =>
      removeCollaborator(id, asAcExpo),
    onSuccess: (_data, { asAcExpo }) => {
      queryClient.invalidateQueries({
        queryKey: collaboratorQueryKeys.lists(asAcExpo),
      });
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        throw {
          ...error,
          message: error.response?.data.message,
        };
      }
    },
  });
};

const updateCollaborator = async (
  data: UpdateCollaboratorForm,
  collaboratorId: string,
  asAcExpo?: boolean
) => {
  const url = asAcExpo ? AC_EXPO_COLLABORATOR_URL : COLLABORATOR_BASE_URL;

  const { data: response } = await privateAxios.patch(
    `${url}/${collaboratorId}`,
    data
  );
  return response;
};

export const useUpdateCollaboratorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      collaboratorId,
      asAcExpo,
    }: {
      data: UpdateCollaboratorForm;
      collaboratorId: string;
      asAcExpo?: boolean;
    }) => updateCollaborator(data, collaboratorId, asAcExpo),
    onSuccess: (_data, { asAcExpo }) => {
      queryClient.invalidateQueries({
        queryKey: collaboratorQueryKeys.lists(asAcExpo),
      });
    },
  });
};

const resendAcExpoCollaboratorEmail = async (collaboratorId: string) => {
  const { data: response } = await privateAxios.post(
    `${AC_EXPO_COLLABORATOR_URL}/${collaboratorId}/resend-invitation`
  );
  return response;
};

export const useResendAcExpoCollaboratorEmailMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ collaboratorId }: { collaboratorId: string }) =>
      resendAcExpoCollaboratorEmail(collaboratorId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: collaboratorQueryKeys.lists(true),
      });
    },
  });
};
